import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowTopRightOnSquareIcon,
  EnvelopeIcon,
  EyeIcon,
  PencilSquareIcon,
  ReceiptRefundIcon,
  SquaresPlusIcon,
} from '@heroicons/react/24/outline';
import { Send } from 'lucide-react';

import Dash from '@dashboard/components/Dash';
import EmailCustomerDialog from '@dashboard/components/EmailCustomerDialog';
import FieldEditDialog from '@dashboard/components/FieldEditDialog';
import Link from '@dashboard/components/Link';
import {
  Action,
  ActionGroup,
  CancelChargeDialog,
  ChargeAlerts,
  ItemGroup,
  ItemRow,
  OrderAbandonmentCard,
  OrderActionsColumn,
  OrderCustomerCard,
  OrderFieldsCard,
  OrderLicenseKeyCard,
  OrderPaymentBlock,
  OrderQueryParametersCard,
  OrderTableCard,
  resendConfirmationOffReason,
  sortDetailRows,
  TotalsList,
  actionClass,
  joinDetails,
  stripeIdRow,
  hasPrimarySetupFeeLine,
  nestSetupFees,
  setupFeeSubLine,
  RecordPaymentAction,
} from '@dashboard/components/OrderPage';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import useLicenseKeyQueries from '@dashboard/hooks/queries/useLicenseKeyQueries';
import { buildEmailVariables } from '@dashboard/lib/emailVariables';
import { formatDateTime } from '@dashboard/lib/format-date';
import { confirm } from '@dashboard/lib/confirm';
import { chargeEmailLogsKey, chargeKey } from '@dashboard/lib/queryKeys';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';
import { formatPrice } from '@dashboard/services/formatting';
import getPageTypeLabel, { getPageTypeHeading } from '@dashboard/services/getPageTypeLabel';
import { fetcher } from '@dashboard/services/request';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import { getPaymentMethodLabel } from '@repo/lib';
import PaymentsChargeRefundDialog from '../components/PaymentsChargeRefundDialog';
import {
  getBulkOff,
  getBulkTier,
  getChargeTotals,
  getVariantRows,
  hasOrderItems,
} from '../chargeTotals';
import { usePageRecordHref } from '@dashboard/lib/detailLinks';
import { formatOrderId } from '@dashboard/lib/orderId';

/** An order item as a row; its setup fee line, when the order has one, is a sub-line under it. */
const itemRow = ({ item, setupFee }, money) => ({
  name: item.name,
  imageUrl: item.imageUrl,
  details: `${item.quantity} × ${money(item.unitPrice)}`,
  // item.amount holds the variant options and is net of the bulk discount; both have their own rows.
  amount: money(item.unitPrice * item.quantity),
  sub: setupFee ? setupFeeSubLine(setupFee, money) : null,
});

/** Product, Variants and Order bumps rows, then the totals: the old Payment, Items, Bulk discount, Coupon and Variants cards in one block. */
export const ChargeItems = ({ payment }) => {
  const { currency } = payment;
  const money = amount => getPriceLabel({ amount, currency });
  const negative = amount => getPriceLabel({ amount, currency, isNegated: true });
  const items = payment.orderItems ?? [];
  const nested = nestSetupFees(items);
  const legacy = !hasOrderItems(payment);
  const products = legacy
    ? [
        {
          name: payment.page?.productTitle || <Dash />,
          details: payment.productPrice ? 'Product price' : null,
          amount: payment.productPrice ? money(payment.productPrice) : null,
        },
      ]
    : nested.filter(row => !row.item.isOrderBump).map(row => itemRow(row, money));
  const bumps = nested.filter(row => row.item.isOrderBump).map(row => itemRow(row, money));
  // A setup fee line in the order replaces the record's own Setup fee sub-line (same figure).
  const hasSetupFeeItem = hasPrimarySetupFeeLine(items);
  const variants = getVariantRows(payment);
  const hasImages = items.some(item => item.imageUrl);
  const href = payment.page?._id ? `/pages/${payment.page._id}` : undefined;

  const { rows } = getChargeTotals(payment);
  const totals = rows.map(row => {
    const value =
      row.kind === 'discounted-from' ? (
        <span className="line-through">{money(row.amount)}</span>
      ) : row.amount < 0 ? (
        negative(-row.amount)
      ) : (
        money(row.amount)
      );
    let label = row.label;
    if (row.kind === 'bulk')
      label = (
        <>
          <Link to={`/pages/${payment.page?._id}/customize/product/bulk-discounts`}>
            Bulk discount
          </Link>
          {payment.discount
            ? `: ${getBulkTier(payment.discount)}, ${getBulkOff(payment.discount, currency)}`
            : ''}
        </>
      );
    if (row.kind === 'coupon')
      label = (
        <>
          Discount: {payment.coupon.label} (
          <Link to={`/coupons/${payment.coupon._id}`}>{payment.coupon.code}</Link>
          {payment.coupon.percentOff ? `, ${payment.coupon.percentOff}% off` : ''})
        </>
      );
    return { key: row.key, label, value, bold: row.bold, rule: row.rule };
  });

  return (
    <>
      <ItemGroup title={products.length > 1 ? 'Products' : 'Product'}>
        {products.map((product, index) => (
          <ItemRow
            key={index}
            showImage={hasImages}
            imageUrl={product.imageUrl}
            name={product.name}
            href={href}
            details={product.details}
            amount={product.amount}
            sub={
              payment.setupFee && !hasSetupFeeItem && index === 0
                ? {
                    label: 'Setup fee',
                    amount: formatPrice({ amount: payment.setupFee, currency }),
                  }
                : product.sub
            }
          />
        ))}
      </ItemGroup>
      {variants.length > 0 && (
        <ItemGroup title="Variants">
          {variants.map(variant => (
            <ItemRow
              key={variant.key}
              showImage={hasImages}
              name={variant.name}
              details={joinDetails([
                `${variant.quantity} × ${money(variant.unitPrice)}`,
                variant.bulkOff > 0 ? `${money(variant.bulkOff)} off each (bulk discount)` : null,
                variant.payWhatYouWant ? 'Pay what you want' : null,
              ])}
              amount={money(variant.amount)}
            />
          ))}
        </ItemGroup>
      )}
      {bumps.length > 0 && (
        <ItemGroup title="Order bumps">
          {bumps.map((item, index) => (
            <ItemRow
              key={index}
              showImage={hasImages}
              imageUrl={item.imageUrl}
              name={item.name}
              href={href}
              details={item.details}
              amount={item.amount}
              sub={item.sub}
            />
          ))}
        </ItemGroup>
      )}
      <TotalsList totals={totals} />
    </>
  );
};

const PaymentsChargesDetail = ({ payment }) => {
  const recordHref = usePageRecordHref();
  const { seller, isAdminLoggedInAsSeller, token } = useAuth();
  const { toggleLicenseKey } = useLicenseKeyQueries();
  const [isTogglingLicenseKey, setIsTogglingLicenseKey] = useState(false);
  const queryClient = useQueryClient();
  const { currency } = payment;

  const { mutateAsync: updateFields } = useMutation({
    mutationFn: async values =>
      fetcher(`/sellers/${seller._id}/charges/${payment._id}`, token, {
        method: 'PUT',
        body: JSON.stringify({ fields: values }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chargeKey(payment._id) });
      toast({ title: 'Fields updated' });
    },
  });

  const { mutate: sendChargeConfirmationEmail, isPending: isSendingChargeConfirmationEmail } =
    useMutation({
      mutationFn: async () =>
        fetcher(`/sellers/${seller._id}/charges/${payment._id}/send-confirmation-email`, token, {
          method: 'POST',
        }),
      onSuccess: () => toast({ title: 'Confirmation email resent' }),
      onError: error =>
        toast({
          title: 'Failed to send confirmation email',
          description: error?.message,
          type: 'error',
        }),
    });

  const handleSendChargeConfirmationEmail = async () => {
    const confirmed = await confirm('Send a new confirmation email to the customer?', {
      confirm: 'Send',
    });
    if (confirmed) sendChargeConfirmationEmail();
  };

  async function handleToggleLicenseKey() {
    setIsTogglingLicenseKey(true);
    const action = payment.licenseKey.enabled ? 'disable' : 'enable';
    const { error } = await toggleLicenseKey(payment.licenseKey._id, action);
    if (error) {
      setIsTogglingLicenseKey(false);
      window.alert(`Failed to ${action} license key. Try again or contact support.`);
      return;
    }
    queryClient.invalidateQueries({ queryKey: chargeKey(payment._id) });
    setIsTogglingLicenseKey(false);
  }

  const stripe = { livemode: payment.livemode, seller, isAdminLoggedInAsSeller };
  const details = [
    { label: 'Order ID', value: formatOrderId(payment.orderId), copy: payment.orderId },
    {
      label: 'Payment method',
      value: payment.paymentMethod ? (
        getPaymentMethodLabel(payment.paymentMethod, { includeBrand: false })
      ) : (
        <Dash />
      ),
      copy: payment.paymentMethod
        ? getPaymentMethodLabel(payment.paymentMethod, { includeBrand: false })
        : null,
    },
    { label: 'SKU', value: payment.sku || <Dash />, copy: payment.sku || null },
    ...(payment.feeAmount
      ? [
          {
            label: 'Checkout Page fee',
            value: formatPrice({ amount: payment.feeAmount, currency }),
          },
        ]
      : []),
    {
      label: 'Customer ID',
      value: payment.customer ? `${payment.customer._id}` : <Dash />,
      copy: payment.customer ? `${payment.customer._id}` : null,
    },
    { label: 'Payment ID', value: `${payment._id}` },
    stripeIdRow({
      label: 'Stripe customer ID',
      id: payment.customer?.stripeCustomerId,
      path: `/customers/${payment.customer?.stripeCustomerId}`,
      ...stripe,
    }),
    stripeIdRow({
      label: 'Stripe charge ID',
      id: payment.chargeId,
      path: `/payments/${payment.chargeId}`,
      ...stripe,
    }),
    { label: 'Payment type', value: 'One-time' },
    {
      label: 'Prefilled',
      value: payment.isPrefilled ? 'Is prefilled' : '—',
      copy: payment.isPrefilled ? 'Is prefilled' : null,
    },
    { label: 'Created', value: formatDateTime(payment.createdAt) },
  ];

  const stripeOff = !payment.livemode || !payment.chargeId;
  const resendOffReason = resendConfirmationOffReason(payment, 'payment');

  return (
    <>
      <ChargeAlerts payment={payment} canceledTitle="Payment canceled" showCanceledAt />

      <div className="flex flex-col lg:flex-row max-w-5xl items-start justify-center">
        <div className="order-2 lg:order-1 w-full lg:pr-8">
          <OrderPaymentBlock>
            <ChargeItems payment={payment} />
          </OrderPaymentBlock>
          <OrderAbandonmentCard record={payment} seller={seller} />
          <OrderLicenseKeyCard
            licenseKey={payment.licenseKey}
            onToggle={handleToggleLicenseKey}
            toggling={isTogglingLicenseKey}
          />
          <OrderCustomerCard customer={payment.customer} />
          <OrderFieldsCard
            fields={payment.fields}
            page={payment.page}
            updateFields={updateFields}
          />
          <OrderTableCard title="Details" rows={sortDetailRows(details)} />
          <OrderQueryParametersCard queryParameters={payment.queryParameters} />
        </div>

        {/* 54px = the card's padding (16) + the Payment heading (26) + the grey box's top margin (12), so both boxes start level. */}
        <div className="order-1 lg:order-2 w-full lg:w-72 shrink-0 px-4 pt-3 lg:pt-[52px]">
          <OrderActionsColumn>
            <ActionGroup title="Payment">
              <RecordPaymentAction payment={payment} />
              {payment.stripePaymentIntentId ? (
                <PaymentsChargeRefundDialog payment={payment} />
              ) : (
                <Action
                  icon={ReceiptRefundIcon}
                  label="Refund payment"
                  off
                  title="No Stripe payment to refund"
                />
              )}
              <CancelChargeDialog payment={payment} noun="payment" />
              <Action
                icon={ArrowTopRightOnSquareIcon}
                label="View in Stripe"
                to={getStripeDashboardUrl(`/payments/${payment.chargeId}`, {
                  seller,
                  isAdminLoggedInAsSeller,
                })}
                blank
                off={stripeOff}
                title="Only for live payments"
              />
            </ActionGroup>
            <ActionGroup title="Email">
              <Action
                icon={EnvelopeIcon}
                label="Resend confirmation"
                onClick={handleSendChargeConfirmationEmail}
                off={Boolean(resendOffReason)}
                title={resendOffReason}
                loading={isSendingChargeConfirmationEmail}
              />
              {payment.customer?.email ? (
                <EmailCustomerDialog
                  customerEmail={payment.customer.email}
                  livemode={payment.livemode}
                  objectLabel="payments"
                  endpoint={`/sellers/${seller._id}/charges/${payment._id}/send-email`}
                  variables={buildEmailVariables({ page: payment.page })}
                  queryKey={chargeEmailLogsKey(payment._id)}
                  emailsPath={`/payments/${payment._id}/emails`}
                />
              ) : (
                <Action
                  icon={Send}
                  label="Email customer"
                  off
                  title="The customer has no email address"
                />
              )}
            </ActionGroup>
            <ActionGroup title="Customer">
              <Action
                icon={EyeIcon}
                label="View customer"
                to={payment.customer ? recordHref('customers', payment.customer._id) : '#'}
                off={!payment.customer}
                title="No customer on this payment"
              />
            </ActionGroup>
            <ActionGroup title="Fields">
              <FieldEditDialog
                fields={payment.fields}
                page={payment.page}
                updateFields={updateFields}
                trigger={
                  <Link className={actionClass(false, 'cursor-pointer')} onClick={() => {}}>
                    <>
                      <PencilSquareIcon className="w-4 h-4" />
                      Edit fields
                    </>
                  </Link>
                }
              />
            </ActionGroup>
            <ActionGroup title={getPageTypeHeading(payment.page)}>
              <Action
                icon={SquaresPlusIcon}
                label={`View ${getPageTypeLabel(payment.page)}`}
                to={payment.page?._id ? `/pages/${payment.page._id}` : '#'}
                off={!payment.page?._id}
                title="This payment has no page"
              />
            </ActionGroup>
          </OrderActionsColumn>
        </div>
      </div>
    </>
  );
};

export default PaymentsChargesDetail;
